import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { ERROR_KEYS, LOGALTO_SIGNUP_URL } from '@/lib/constants';
import type { Translation } from '@/lib/i18n';
import { LoaderCircle } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';

type ErrorKey = (typeof ERROR_KEYS)[number];

type Props = {
	platformHost?: string;
	translation: Translation;
	onSubmit: (subdomain: string) => Promise<{ error?: string }>;
};

export function SubdomainForm({ platformHost, translation, onSubmit }: Props) {
	const [subdomain, setSubdomain] = useState('');
	const [error, setError] = useState<ErrorKey>();
	const [loading, setLoading] = useState(false);
	const submit = async (event: SubmitEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(undefined);
		setLoading(true);
		const result = await onSubmit(subdomain);
		setError(result.error ? (ERROR_KEYS.find((key) => translation[key] === result.error) ?? 'unknown') : undefined);
		setLoading(false);
	};

	return (
		<form
			onSubmit={(event) => void submit(event)}
			className="mt-11 grid gap-3"
		>
			<Label
				className="text-base font-semibold text-[#16221f]"
				htmlFor="subdomain"
			>
				{translation.subdomainLabel}
			</Label>

			<InputGroup className="h-16 rounded-lg border-primary bg-white focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/20">
				<InputGroupInput
					id="subdomain"
					value={subdomain}
					onChange={(event) => setSubdomain(event.target.value)}
					autoComplete="organization"
					autoFocus
					placeholder="acme"
					aria-invalid={!!error}
					className="h-full border-0 text-base text-[#68758f] shadow-none focus-visible:border-0 focus-visible:ring-0"
				/>
				<InputGroupAddon align="inline-end">
					<InputGroupText className="text-base font-semibold text-[#68758f]">
						{platformHost ? `.${platformHost}` : '…'}
					</InputGroupText>
				</InputGroupAddon>
			</InputGroup>

			{error && (
				<Alert
					variant="destructive"
					className="mt-0.5 border-0 bg-transparent px-0 py-0 text-xs shadow-none"
				>
					<AlertDescription className="text-xs">{translation[error]}</AlertDescription>
				</Alert>
			)}

			<Button
				type="submit"
				size="lg"
				disabled={loading || !platformHost}
				className="mt-3 h-14 w-full rounded-lg text-base font-semibold"
			>
				{loading ? (
					<LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" />
				) : (
					translation.continue
				)}
			</Button>

			<div className="mt-7 flex items-center gap-5 text-center text-base font-medium text-[#71819d]">
				<span className="h-px flex-1 bg-[#b8c1cb]" />
				<span>{translation.registerPrompt}</span>
				<span className="h-px flex-1 bg-[#b8c1cb]" />
			</div>

			<Button
				asChild
				variant="outline"
				size="lg"
				className="mt-3 h-15 w-full rounded-lg border-primary bg-white text-base font-semibold text-primary hover:bg-primary/10 hover:text-primary-hover active:text-primary-active"
			>
				<a href={LOGALTO_SIGNUP_URL}>{translation.register}</a>
			</Button>
		</form>
	);
}
